import { useRef, useState, useEffect, useCallback } from 'react'
import { Button, Label, Slider } from '@creanizer/ui'
import { Paintbrush, Eraser, Trash2 } from 'lucide-react'

interface MaskCanvasProps {
  referenceImage: string
  onMaskChange: (maskDataUrl: string | null) => void
  width?: number
  height?: number
}

export function MaskCanvas({
  referenceImage,
  onMaskChange,
  width = 512,
  height = 512,
}: MaskCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [brushSize, setBrushSize] = useState(30)
  const [tool, setTool] = useState<'brush' | 'eraser'>('brush')
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 })

  // 画像を読み込んでキャンバスサイズを調整
  useEffect(() => {
    const img = new Image()
    img.onload = () => {
      const aspectRatio = img.width / img.height
      let canvasWidth = width
      let canvasHeight = height

      if (aspectRatio > 1) {
        canvasHeight = width / aspectRatio
      } else {
        canvasWidth = height * aspectRatio
      }

      setImageDimensions({ width: canvasWidth, height: canvasHeight })
      setImageLoaded(true)

      // キャンバスを初期化
      const canvas = canvasRef.current
      if (canvas) {
        canvas.width = canvasWidth
        canvas.height = canvasHeight
        const ctx = canvas.getContext('2d')
        if (ctx) {
          ctx.clearRect(0, 0, canvasWidth, canvasHeight)
        }
      }
    }
    img.src = referenceImage
  }, [referenceImage, width, height])

  // マスクデータをエクスポート
  const exportMask = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // 元のキャンバスのピクセルデータを取得
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const data = imageData.data

    // 何も描画されていない場合はnullを返す
    const hasContent = data.some((value, index) => index % 4 === 3 && value > 0)

    if (!hasContent) {
      onMaskChange(null)
      return
    }

    // マスク画像を生成（完全な白黒に変換）
    const maskCanvas = document.createElement('canvas')
    maskCanvas.width = canvas.width
    maskCanvas.height = canvas.height
    const maskCtx = maskCanvas.getContext('2d')

    if (maskCtx) {
      const maskImageData = maskCtx.createImageData(canvas.width, canvas.height)
      const maskData = maskImageData.data

      // ピクセルごとに処理：アルファ値が0より大きければ白、そうでなければ黒
      for (let i = 0; i < data.length; i += 4) {
        const alpha = data[i + 3]
        if (alpha > 0) {
          // 描画された部分は白
          maskData[i] = 255     // R
          maskData[i + 1] = 255 // G
          maskData[i + 2] = 255 // B
          maskData[i + 3] = 255 // A
        } else {
          // 描画されていない部分は黒
          maskData[i] = 0       // R
          maskData[i + 1] = 0   // G
          maskData[i + 2] = 0   // B
          maskData[i + 3] = 255 // A
        }
      }

      maskCtx.putImageData(maskImageData, 0, 0)
      const maskDataUrl = maskCanvas.toDataURL('image/png')
      console.log('[MaskCanvas] Exported mask, length:', maskDataUrl.length)
      onMaskChange(maskDataUrl)
    }
  }, [onMaskChange])

  // 座標を取得して描画する内部関数
  const drawAt = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const rect = canvas.getBoundingClientRect()
    let clientX: number, clientY: number

    if ('touches' in e) {
      if (e.touches.length === 0) return
      clientX = e.touches[0].clientX
      clientY = e.touches[0].clientY
    } else {
      clientX = e.clientX
      clientY = e.clientY
    }

    const x = (clientX - rect.left) * (canvas.width / rect.width)
    const y = (clientY - rect.top) * (canvas.height / rect.height)

    ctx.beginPath()
    ctx.arc(x, y, brushSize / 2, 0, Math.PI * 2)

    if (tool === 'brush') {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
      ctx.fill()
    } else {
      ctx.globalCompositeOperation = 'destination-out'
      ctx.fill()
      ctx.globalCompositeOperation = 'source-over'
    }
  }

  // 描画開始
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    setIsDrawing(true)
    drawAt(e)
  }

  // 描画中（マウス移動時）
  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return
    drawAt(e)
  }

  // 描画終了
  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false)
      exportMask()
    }
  }

  // マスクをクリア
  const clearMask = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.clearRect(0, 0, canvas.width, canvas.height)
    onMaskChange(null)
  }

  if (!imageLoaded) {
    return <div className="text-center py-4 text-gray-500">画像を読み込み中...</div>
  }

  return (
    <div className="space-y-3">
      <Label>マスク編集（白く塗った部分が編集対象）</Label>

      {/* ツールバー */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          size="sm"
          variant={tool === 'brush' ? 'default' : 'outline'}
          onClick={() => setTool('brush')}
        >
          <Paintbrush className="h-4 w-4 mr-1" />
          ブラシ
        </Button>
        <Button
          size="sm"
          variant={tool === 'eraser' ? 'default' : 'outline'}
          onClick={() => setTool('eraser')}
        >
          <Eraser className="h-4 w-4 mr-1" />
          消しゴム
        </Button>
        <Button size="sm" variant="ghost" onClick={clearMask}>
          <Trash2 className="h-4 w-4 mr-1" />
          クリア
        </Button>

        <div className="flex items-center gap-2 ml-auto">
          <Label className="text-xs">サイズ: {brushSize}</Label>
          <Slider
            value={[brushSize]}
            onValueChange={(v) => setBrushSize(v[0])}
            min={5}
            max={100}
            step={5}
            className="w-24"
          />
        </div>
      </div>

      {/* キャンバスエリア */}
      <div
        className="relative border rounded overflow-hidden select-none"
        style={{ width: imageDimensions.width, height: imageDimensions.height }}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* 背景画像 */}
        <img
          src={referenceImage}
          alt="Reference"
          draggable={false}
          className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
          style={{ userSelect: 'none', WebkitUserDrag: 'none' } as React.CSSProperties}
        />

        {/* マスク描画用キャンバス */}
        <canvas
          ref={canvasRef}
          className="absolute inset-0 cursor-crosshair"
          style={{
            width: imageDimensions.width,
            height: imageDimensions.height,
            zIndex: 10,
          }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />

        {/* マスクオーバーレイ表示（視認性向上） */}
        <div
          className="absolute inset-0 pointer-events-none bg-red-500/20"
          style={{ mixBlendMode: 'multiply', zIndex: 5 }}
        />
      </div>

      <p className="text-xs text-gray-500">
        変更したい部分をブラシで塗りつぶしてください。塗られた部分のみがプロンプトに従って変更されます。
      </p>
    </div>
  )
}
